import axios from 'axios';
import { API_BASE_URL } from '../config';
import { getAuthToken, getAuthTokens } from '../utils/auth';
import api from './middleware';

// Axios instance with auth header
const authAxios = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
authAxios.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

export const apiEndpoints = {
  // Start session with test case description
  startSession: async (url, testDescription) => {
    try {
      const response = await api.post('/start', { 
        url, 
        testcase: testDescription 
      });
      return response.data;
    } catch (error) {
      console.error('Error calling start endpoint:', error);
      throw error;
    }
  },

  // Get the screenshots for a specific session
  getSessionScreenshots: async (sessionId) => {
    try {
      const response = await api.get(`/session/screenshots/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching screenshots:', error);
      throw error;
    }
  },

  // Get the latest screenshot for a session
  getLatestScreenshot: async (sessionId) => {
    try {
      const response = await api.get(`/api/session/latest-screenshot/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Latest screenshot error:', error);
      throw error;
    }
  },

  // Create test suite
  createTestSuite: async (testSuiteData) => {
    try {
      const { refreshToken } = getAuthTokens();

      if (!refreshToken) {
        console.error('Refresh token is missing');
        throw new Error('Please log in again - missing refresh token');
      }

      const response = await api.post('/api/testsuite/create', 
        {
          ...testSuiteData,
          refresh_token: refreshToken
        }
      );
      return response.data;
    } catch (error) {
      console.error('Test suite creation error:', error);
      throw error;
    }
  },

  // Get test suites
  getTestSuites: async () => {
    try {
      const response = await api.get('/api/testsuite/list');
      return response.data;
    } catch (error) {
      console.error('Test suite list error:', error);
      throw error;
    }
  },

  // Get test cases for a specific test suite
  getTestCases: async (suiteId, sortBy) => {
    try {
      // Only add sort parameter if sortBy has a value
      const url = sortBy 
        ? `/api/testsuite/${suiteId}/testcases?sort=${sortBy}`
        : `/api/testsuite/${suiteId}/testcases`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error in getTestCases:', error);
      throw error;
    }
  },

  // Create a new test case for a specific test suite
  createTestCase: async (suiteId, testCaseData) => {
    try {
      const response = await api.post(
        `/api/testsuite/${suiteId}/testcase`,
        testCaseData
      );
      return response.data;
    } catch (error) {
      console.error('Test case creation error:', error);
      throw error;
    }
  },

  // Run a specific test case
  runTestCase: async (suiteId, testId) => {
    try {
      const response = await api.post(
        `/testsuite/${suiteId}/tests/${testId}/run`,
        {}
      );
      return response.data;
    } catch (error) {
      console.error('Test run error:', error);
      throw error;
    }
  },

  // Get a specific test suite
  getTestSuite: async (suiteId) => {
    try {
      const response = await api.get(`/api/testsuite/${suiteId}/testcases`);
      return response.data;
    } catch (error) {
      console.error('Test suite fetch error:', error);
      throw error;
    }
  },

  // Start a new test session
  startTestSession: async (suiteId, testCaseData) => {
    try {
      const response = await api.post(
        '/api/start',
        {
          testSuiteId: suiteId,
          testcase: testCaseData
        }
      );
      return response.data;
    } catch (error) {
      console.error('Start session error:', error);
      throw error;
    }
  },

  runTestSuite: async (suiteId) => {
    try {
      // First create a job for this test suite run
      const jobResponse = await api.post(
        '/api/job/create', 
        { test_suite_id: suiteId }
      );

      const jobId = jobResponse.data.id;

      // Then start executing the test suite
      const response = await api.post(
        `/api/testsuite/${suiteId}/run`,
        { job_id: jobId }
      );

      return {
        ...response.data,
        job_id: jobId
      };
    } catch (error) {
      console.error('Run test suite error:', error);
      throw error;
    }
  },

  // Update the executeBrowserStep function to use a consistent payload structure
  executeBrowserStep: async (step, suiteId, sessionId) => {
    try {
      // Create a structured payload with a consistent format
      let stepData;
      
      // Format the step data based on action type
      switch (step.action) {
        case 'type':
          stepData = {
            action: 'type',
            element: step.element,
            value: step.value,
            secret: step.secret,
            value_mode: step.value_mode || 'static'
          };
          break;
        case 'click':
          stepData = {
            action: 'click',
            element: step.element
          };
          break;
        case 'navigate':
          stepData = {
            action: 'navigate',
            value: step.value
          };
          break;
        case 'workflow':
          stepData = {
            action: 'workflow',
            workflowId: step.workflowId
          };
          break;
        case 'select':
          stepData = {
            action: 'select',
            element: step.element,
            value: step.value
          };
          break;
        case 'enter':
          stepData = {
            action: 'enter'
          };
          break;
        default:
          stepData = {
            action: step.action,
            element: step.element,
            value: step.value
          };
      }
      
      // Create a consistent payload structure
      const payload = {
        actionStep: stepData,
        test_suite_id: suiteId,
        session_id: sessionId
      };

      const response = await fetch(`${API_BASE_URL}/browser/step`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to execute step');
      }

      const data = await response.json();
      return {
        sessionId: data.session_id,
        status: data.status
      };
    } catch (error) {
      console.error('Execute browser step error:', error);
      throw error;
    }
  },

  // For single step polling
  getStepResult: async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/browser/step/${sessionId}/result`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get step result');
      }

      return await response.json();
    } catch (error) {
      console.error('Get step result error:', error);
      throw error;
    }
  },

  // For batch steps polling
  getBatchStepsResult: async (sessionId) => {
    try {
      const response = await api.get(`/browser/steps/${sessionId}/result`);
      return response.data;
    } catch (error) {
      console.error('Get batch steps result error:', error);
      throw error;
    }
  },

  generateAssertion: async (testname, steps, screenshots) => {
    try {
      // Format each step to have a consistent structure
      const formattedSteps = steps.map((step) => {
        
        // Check if this is the complex format with newStepData
        if (step.newStepData && typeof step.newStepData === 'object') {
          const metadata = step.newStepData.metadata || {};
          
          // Handle workflow case
          if (metadata.actionType === 'workflow' || step.subWorkflowId) {
            return {
              actionType: 'workflow',
              workflowId: metadata.workflowId || step.subWorkflowId
            };
          }
          
          // Handle other actions
          return {
            actionType: metadata.actionType || 'unknown',
            selector: metadata.selector || '',
            value: metadata.value || '',
            exact: metadata.exact || false
          };
        } 
        // Handle direct format
        else if (step.action) {
          // Handle workflow in direct format
          if (step.action === "workflow") {
            return {
              actionType: step.action,
              workflowId: step.workflowId,
            };
          }
  
          // Handle click with exact property
          if (step.action === "click") {
            return {
              actionType: step.action,
              selector: step.element || '',
              exact: step.exact || false,
            };
          }
  
          // For other actions (type, navigate, select, enter)
          return {
            actionType: step.action,
            selector: step.element || '',
            value: step.value || '',
          };
        }
        
        // Fallback for any other format
        return {
          actionType: 'unknown',
          selector: '',
          value: ''
        };
      });
  
      const payload = {
        testname: testname,
        steps: formattedSteps,
        screenshots: screenshots
      };
    
      const response = await api.post('/browser/createAssertion', payload);
      return response.data;
    } catch (error) {
      console.error("Generate assertion error:", error);
      throw error;
    }
  },

  executeBrowserSteps: async (steps, suiteId, sessionId, fixtureId = null) => {
    try {
      // Format each step to have a consistent structure
      const formattedSteps = steps.map(step => {        
        // Handle API request steps
        if (step.action === 'api-request') {
          return {
            actionType: 'api-request',
            endpoint_id: step.endpoint_id
          };
        }
        
        // Format the step data based on action type
        const formatted = {
          actionType: step.action,
          selector: step.element,
          value: step.value,
          exact: step.exact,
          ...(step.action === 'type' && { 
            secret: step.secret,
            value_mode: step.value_mode || 'static'
          }),
          ...(step.action === 'navigate' && {
            value_mode: step.value_mode || 'static'
          }),
          ...(step.step_screenshot && { step_screenshot: step.step_screenshot })
        };

        if (step.action === 'workflow') {
          formatted.workflowId = step.workflowId;
          formatted.repeatCount = step.repeatCount || 1;
          delete formatted.selector;
          delete formatted.value;
          delete formatted.secret;
          delete formatted.value_mode;
        }

        return formatted;
      });


      const payload = {
        steps: formattedSteps,
        test_suite_id: suiteId,
        session_id: sessionId
      };
      
      // Add the fixture ID to the payload if provided
      if (fixtureId) {
        payload.fixture_id = fixtureId;
      }


      const response = await api.post('/browser/steps', payload);
      return response.data;
    } catch (error) {
      console.error('Execute browser steps error:', error);
      throw error;
    }
  },

  // Add this new function to apiEndpoints
  startBrowserSession: async (suiteId) => {
    try {
      const response = await api.get(`/browser/session/start/${suiteId}`);
      return {
        sessionId: response.data.session_id,
        screenshot: response.data.screenshot
      };
    } catch (error) {
      console.error('Start browser session error:', error);
      throw error;
    }
  },

  // Add this method to the apiEndpoints object
  getTestCaseById: async (suiteId, testCaseId) => {
    try {
      const response = await api.get(
        `/api/testsuite/${suiteId}/testcase/${testCaseId}`
      );
      return response.data;
    } catch (error) {
      console.error('Get test case error:', error);
      throw error;
    }
  },

  cancelBrowserSession: async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/browser/session/${sessionId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
  
      if (!response.ok) {
        throw new Error('Failed to cancel browser session');
      }
  
      return await response.json();
    } catch (error) {
      console.error('Cancel browser session error:', error);
      throw error;
    }
  },

  generateSteps: async (testname, screenshot) => {
    try {
      const response = await fetch(`${API_BASE_URL}/browser/generate-test-steps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          test_name: testname,
          screenshot: screenshot
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate test steps');
      }

      const data = await response.json();
      
      if (data.status !== "success") {
        throw new Error(data.message || 'Failed to generate test steps');
      }
      
      return data;
    } catch (error) {
      console.error('Generate steps error:', error);
      throw error;
    }
  },

  // Upload step screenshot to Supabase
  uploadStepScreenshot: async (file, stepId) => {
    try {
      console.log('📸 API: Uploading step screenshot...', { file, stepId });
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('step_id', stepId);
      
      const response = await fetch(`${API_BASE_URL}/upload-step-screenshot`, {
        method: 'POST',
        body: formData,
      });
      
      console.log('📸 API: Upload response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('📸 API: Upload failed:', errorText);
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('📸 API: Upload successful:', result);
      
      return result;
    } catch (error) {
      console.error('📸 API: Upload step screenshot error:', error);
      throw error;
    }
  },

  // Delete step screenshot from Supabase
  deleteStepScreenshot: async (filename) => {
    try {
      const response = await fetch(`${API_BASE_URL}/delete-step-screenshot/${filename}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete screenshot');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Delete step screenshot error:', error);
      throw error;
    }
  },
};

export default apiEndpoints; 